// client/src/components/admin/layout/SystemSettings.jsx
import React, { useState, useEffect, useCallback } from 'react';
import adminService from '../../services/adminService';
import HirsToggleModal from './layout/HirsToggleModal';
import '../../styles/components/admin/SystemSettings.css'; 

import dashboardIcon from '../../assets/logo/dashboard_systemIcon.svg';
import patientsIcon from '../../assets/logo/patient_systemIcon.svg';
import journalIcon from '../../assets/logo/journal_systemIcon.svg';
import templateIcon from '../../assets/logo/journal_systemIcon.svg';
import appointmentsIcon from '../../assets/logo/appointments_systemIcon.svg';
import messagesIcon from '../../assets/logo/message_systemIcon.svg';


const SystemSettings = () => {
  // State management
  const [selectedTenant, setSelectedTenant] = useState('');
  const [tenants, setTenants] = useState([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingSection, setUploadingSection] = useState('');
  
  // 🔄 NEW: Modal state for HIRS toggle
  const [modalState, setModalState] = useState({
    isOpen: false,
    hirsSetting: null,
    action: null, // 'enable' or 'disable'
    isLoading: false
  });
  
  // Preview URLs state for ALL image types (Logo + Favicon)
  const [previewUrls, setPreviewUrls] = useState({
    lightLogo: '',
    darkLogo: '',
    lightFavicon: '',
    darkFavicon: ''
  });

  const [selectedFileNames, setSelectedFileNames] = useState({
  lightLogo: '',
  darkLogo: '',
  lightFavicon: '',
  darkFavicon: ''
});
  
  // Settings state
  const [settings, setSettings] = useState({
    platformName: '',
    platformDescription: '',
    systemLogo: { light: null, dark: null },
    favicon: { light: null, dark: null },
    primaryColor: '',
    secondaryColor: '',
    hirsSettings: []
  });

  // 🔄 Broadcast settings update to all open tabs/windows
  const broadcastSettingsUpdate = useCallback((tenantId, updatedSettings) => {
    console.log('📡 Broadcasting settings update to all tabs...');
    
    // Method 1: Custom event for same tab
    const updateEvent = new CustomEvent('tenantSettingsUpdated', {
      detail: {
        tenantId,
        updatedSettings,
        timestamp: Date.now()
      }
    });
    window.dispatchEvent(updateEvent);
    
    // Method 2: LocalStorage event for other tabs
    localStorage.setItem('tenantSettingsUpdated', JSON.stringify({
      tenantId,
      timestamp: Date.now(),
      updatedSettings
    }));
    
    // Clean up the localStorage flag after a short delay
    setTimeout(() => {
      localStorage.removeItem('tenantSettingsUpdated');
    }, 1000);
    
    console.log('✅ Settings update broadcasted successfully');
  }, []);

  // 🔄 ENHANCED: Individual setting save function with real-time updates
  const saveIndividualSetting = async (settingType, value) => {
    if (!selectedTenant) {
      alert('Please select a clinic first');
      return;
    }
    try {
      const updateData = { [settingType]: value };
      const data = await adminService.updateIndividualSetting(selectedTenant, updateData);
      if (data.success) {
        // 🔄 Broadcast the update to all open tabs
        broadcastSettingsUpdate(selectedTenant, { [settingType]: value });
        
        alert(`${settingType.replace(/([A-Z])/g, ' $1').toLowerCase()} saved successfully!`);
        
        // 🔄 Small delay to ensure backend is updated, then refresh preview
        setTimeout(() => {
          fetchTenantSettings();
        }, 500);
      } else {
        throw new Error(data.message || 'Save failed');
      }
    } catch (error) {
      alert(`Failed to save ${settingType}: ${error.response?.data?.message || error.message}`);
    }
  };

  // 🆕 NEW: Toggle HIRS feature function
  const toggleHirsFeature = async (hirsId, newStatus) => {
  if (!selectedTenant) {
    alert('Please select a clinic first');
    return;
  }

  try {
    setModalState(prev => ({ ...prev, isLoading: true }));

    console.log('🔄 [ADMIN] Toggling HIRS feature:', { hirsId, newStatus, selectedTenant });

    // 🚨 FIXED: Use the correct API endpoint that matches your backend routes
    const response = await fetch(`/api/admin/tenant-settings/${selectedTenant}/hirs/${hirsId}/toggle`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
      },
      body: JSON.stringify({
        isActive: newStatus,
        lastUpdated: new Date().toLocaleDateString()
      })
    });

    const data = await response.json();
    console.log('🔄 [ADMIN] API Response:', data);
    
    if (data.success) {
      // Update local state immediately
      const updatedHirsSettings = settings.hirsSettings.map(hirs => 
        hirs.id === hirsId 
          ? { 
              ...hirs, 
              isActive: newStatus, 
              lastUpdated: new Date().toLocaleDateString() 
            }
          : hirs
      );

      setSettings(prev => ({
        ...prev,
        hirsSettings: updatedHirsSettings
      }));

      // 🚨 CRITICAL: Broadcast the update to all doctor tabs
      const updateData = { hirsSettings: updatedHirsSettings };
      
      // Method 1: Custom event for same tab
      window.dispatchEvent(new CustomEvent('tenantSettingsUpdated', {
        detail: {
          tenantId: selectedTenant,
          hirsId,
          isActive: newStatus,
          updatedSettings: updateData,
          timestamp: Date.now()
        }
      }));
      
      // Method 2: localStorage trigger for other tabs
      localStorage.setItem('tenantSettingsUpdated', JSON.stringify({
        tenantId: selectedTenant,
        hirsId,
        isActive: newStatus,
        timestamp: Date.now(),
        updatedSettings: updateData
      }));
      
      // Method 3: Broadcast channel (if supported)
      if (window.BroadcastChannel) {
        const channel = new BroadcastChannel('tenant-settings');
        channel.postMessage({
          type: 'HIRS_TOGGLE',
          tenantId: selectedTenant,
          hirsId,
          isActive: newStatus,
          timestamp: Date.now()
        });
        channel.close();
      }

      // Clean up localStorage after a delay
      setTimeout(() => {
        localStorage.removeItem('tenantSettingsUpdated');
      }, 1000);

      // Show success message
      const featureName = (settings.hirsSettings && Array.isArray(settings.hirsSettings)) 
      ? settings.hirsSettings.find(h => h.id === hirsId)?.name || 'Feature'
      : 'Feature';
      alert(`${featureName} has been ${newStatus ? 'enabled' : 'disabled'} successfully!`);

      // Close modal
      setModalState({ isOpen: false, hirsSetting: null, action: null, isLoading: false });

      // 🔄 Force refresh doctor interfaces by triggering multiple update methods
      console.log('📡 [ADMIN] Broadcasting settings update to all doctor tabs...');
      
      // Additional trigger - force page refresh signal for doctor tabs
      localStorage.setItem('forceRefreshTenantSettings', JSON.stringify({
        tenantId: selectedTenant,
        timestamp: Date.now()
      }));
      
      setTimeout(() => {
        localStorage.removeItem('forceRefreshTenantSettings');
      }, 2000);

    } else {
      throw new Error(data.message || 'Update failed');
    }
  } catch (error) {
    console.error('❌ [ADMIN] Error toggling HIRS feature:', error);
    
    // More specific error messages
    let errorMessage = 'Failed to update feature';
    if (error.message.includes('fetch')) {
      errorMessage = 'Network error - please check your connection';
    } else if (error.message.includes('401')) {
      errorMessage = 'Authentication failed - please login again';
    } else if (error.message.includes('404')) {
      errorMessage = 'API endpoint not found - please contact support';
    } else {
      errorMessage = error.message;
    }
    
    alert(errorMessage);
    setModalState(prev => ({ ...prev, isLoading: false }));
  }
};

  // 🆕 NEW: Handle HIRS toggle button click
  const handleHirsToggleClick = (hirs) => {
    const newStatus = !hirs.isActive;
    const action = newStatus ? 'enable' : 'disable';
    
    setModalState({
      isOpen: true,
      hirsSetting: hirs,
      action: action,
      isLoading: false
    });
  };

  // 🆕 NEW: Handle modal confirmation
  const handleModalConfirm = () => {
    if (modalState.hirsSetting) {
      const newStatus = modalState.action === 'enable';
      toggleHirsFeature(modalState.hirsSetting.id, newStatus);
    }
  };

  // 🆕 NEW: Handle modal close
  const handleModalClose = () => {
    if (!modalState.isLoading) {
      setModalState({ isOpen: false, hirsSetting: null, action: null, isLoading: false });
    }
  };

  // Create default settings
  const createDefaultTenantSettings = useCallback(async () => {
    try {
      const defaultSettings = {
        platformName: 'NEUROLEX',
        platformDescription: 'Neurolex is an AI-powered system that uses Natural Language Processing (NLP) to analyze your journal entries and track your emotional well-being.',
        primaryColor: '#4CAF50',
        secondaryColor: '#2196F3',
        systemLogo: { light: null, dark: null },
        favicon: { light: null, dark: null },
        hirsSettings: [
          { id: 1, icon: '📊', name: 'Dashboard', description: 'Comprehensive dashboard overview displaying patient analytics, recent activities, and key metrics for doctors.', lastUpdated: new Date().toLocaleDateString(), isActive: true },
          { id: 2, icon: '👥', name: 'Patients', description: 'Complete patient management system including patient profiles, medical history, and contact information list view.', lastUpdated: new Date().toLocaleDateString(), isActive: true },
          { id: 3, icon: '📖', name: 'Patient Journal Management', description: 'View, analyze, and manage all patient journal entries with sentiment analysis and emotional tracking capabilities.', lastUpdated: new Date().toLocaleDateString(), isActive: true },
          { id: 4, icon: '📝', name: 'Journal Template Management', description: 'Create, edit, and manage customizable journal templates with prompts and questions for patient self-reflection.', lastUpdated: new Date().toLocaleDateString(), isActive: true },
          { id: 5, icon: '📅', name: 'Appointments', description: 'Schedule, manage, and track appointments with patients including reminders and availability management.', lastUpdated: new Date().toLocaleDateString(), isActive: true },
          { id: 6, icon: '💬', name: 'Messages', description: 'Secure end-to-end encrypted messaging system for confidential communication between doctors and patients.', lastUpdated: new Date().toLocaleDateString(), isActive: true }
        ]
      };
      const data = await adminService.updateTenantSettings(selectedTenant, defaultSettings);
      if (data.success) {
        setSettings(defaultSettings);
        // 🔄 Broadcast the update
        broadcastSettingsUpdate(selectedTenant, defaultSettings);
      }
    } catch (error) {
      console.error('Error creating default settings:', error);
    }
  }, [selectedTenant, broadcastSettingsUpdate]);

  // Fetch tenant settings
  const fetchTenantSettings = useCallback(async () => {
    if (!selectedTenant) return;
    try {
      setIsLoading(true);
      const data = await adminService.getTenantSettings(selectedTenant);
      if (data.success) {
        const fetchedSettings = {
          platformName: data.data.platformName || 'NEUROLEX',
          platformDescription: data.data.platformDescription || 'Mental wellness platform',
          systemLogo: data.data.systemLogo || { light: null, dark: null },
          favicon: data.data.favicon || { light: null, dark: null },
          primaryColor: data.data.primaryColor || '#4CAF50',
          secondaryColor: data.data.secondaryColor || '#2196F3',
          hirsSettings: data.data.hirsSettings || []
        };
        setSettings(fetchedSettings);
        setPreviewUrls({
          lightLogo: fetchedSettings.systemLogo?.light || '',
          darkLogo: fetchedSettings.systemLogo?.dark || '',
          lightFavicon: fetchedSettings.favicon?.light || '',
          darkFavicon: fetchedSettings.favicon?.dark || ''
        });
      } else {
        await createDefaultTenantSettings();
      }
    } catch (error) {
      await createDefaultTenantSettings();
    } finally {
      setIsLoading(false);
    }
  }, [selectedTenant, createDefaultTenantSettings]);

  // Load tenants
  useEffect(() => {
    const fetchTenants = async () => {
      try {
        setIsLoadingTenants(true);
        const data = await adminService.getTenants();
        if (data.success) {
          setTenants(data.data);
          if (data.data.length === 1) {
            setSelectedTenant(data.data[0]._id);
          }
        }
      } catch (error) {
        console.error('Error fetching tenants:', error);
      } finally {
        setIsLoadingTenants(false);
      }
    };
    fetchTenants();
  }, []);

  useEffect(() => {
    if (selectedTenant) {
      fetchTenantSettings();
    }
  }, [selectedTenant, fetchTenantSettings]);

  const handleInputChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  // 🔄 ENHANCED: File upload function with real-time updates
  const handleFileUpload = async (imageType, variant, file) => {
    if (!file || !selectedTenant) {
      alert('Please select a clinic first');
      return;
    }
    const sectionKey = `${variant}${imageType === 'logo' ? 'Logo' : 'Favicon'}`;
    try {
      setUploadingSection(sectionKey);
      if (!file.type.startsWith('image/')) {
        throw new Error('Please select an image file');
      }
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must be less than 10MB');
      }
      if (imageType === 'favicon' && file.size > 2 * 1024 * 1024) {
        throw new Error('Favicon file size should be less than 2MB');
      }

      // Create immediate preview
      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        const previewKey = `${variant}${imageType === 'logo' ? 'Logo' : 'Favicon'}`;
        setPreviewUrls(prev => ({ ...prev, [previewKey]: e.target.result }));
      };
      fileReader.readAsDataURL(file);

      // Upload to server
      const formData = new FormData();
      formData.append('logo', file);
      formData.append('logoType', variant);
      formData.append('imageType', imageType);
      formData.append('tenantId', selectedTenant);

      const response = await fetch('/api/admin/upload-logo', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        // 🔄 Update preview with new URL immediately
        setTimeout(() => {
          const previewKey = `${variant}${imageType === 'logo' ? 'Logo' : 'Favicon'}`;
          setPreviewUrls(prev => ({ ...prev, [previewKey]: data.url + '?t=' + Date.now() }));
        }, 500);

        const updatedImageData = {
          [variant]: data.url
        };

        setSettings(prev => ({
          ...prev,
          [imageType === 'logo' ? 'systemLogo' : 'favicon']: {
            ...prev[imageType === 'logo' ? 'systemLogo' : 'favicon'],
            [variant]: data.url
          }
        }));

        // 🔄 Broadcast the image update to all open tabs
        const updateData = {
          [imageType === 'logo' ? 'systemLogo' : 'favicon']: {
            ...settings[imageType === 'logo' ? 'systemLogo' : 'favicon'],
            [variant]: data.url
          }
        };
        broadcastSettingsUpdate(selectedTenant, updateData);

        alert(`${imageType} uploaded successfully!`);

        // Auto-save
        try {
          await adminService.updateIndividualSetting(selectedTenant, updateData);
        } catch (saveError) {
          console.warn('Upload successful but auto-save failed:', saveError);
        }
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      alert(`Upload failed: ${error.message}`);
    } finally {
      setUploadingSection('');
    }
  };

  // 🔄 ENHANCED: Save settings with real-time updates
  const saveSettings = async () => {
    if (!selectedTenant) {
      alert('Please select a clinic first');
      return;
    }
    try {
      setIsSaving(true);
      const data = await adminService.updateTenantSettings(selectedTenant, settings);
      if (data.success) {
        // 🔄 Broadcast the complete settings update
        broadcastSettingsUpdate(selectedTenant, settings);
        
        alert('Settings saved successfully!');
        
        // 🔄 Small delay to ensure all tabs receive the update
        setTimeout(() => {
          fetchTenantSettings();
        }, 500);
      } else {
        throw new Error(data.message || 'Save failed');
      }
    } catch (error) {
      alert(`Failed to save settings: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const resetSettings = () => {
    if (selectedTenant) {
      fetchTenantSettings();
      alert('Settings reset to saved values');
    }
  };

  // 🔄 Show notification when settings are updated from another tab
  useEffect(() => {
    const handleSettingsUpdate = () => {
      console.log('🔔 Settings updated from another source, refreshing...');
      if (selectedTenant) {
        fetchTenantSettings();
      }
    };

    window.addEventListener('tenantSettingsUpdated', handleSettingsUpdate);
    return () => window.removeEventListener('tenantSettingsUpdated', handleSettingsUpdate);
  }, [selectedTenant, fetchTenantSettings]);

  const getIconForHirs = (hirsId) => {
    const iconMap = {
      1: dashboardIcon,
      2: patientsIcon,
      3: journalIcon,
      4: templateIcon,
      5: appointmentsIcon,
      6: messagesIcon
    };
    return iconMap[hirsId] || dashboardIcon;
  };

  return (
    <>
      <div className="system-settings-container">
        
        {/* Header */}
        <div className="header-section">
          <h1 className="Sys-Settings-header-title">System Settings</h1>
          <p className="Sys-Settings-header-description">Customize platform appearance, preferences, and help content in one place.</p>
        </div>

        <div className="system-settings-max-width">
          {/* Tenant Selection */}
          <div className="Sys-Settings-tenant-selection">
            <h2 className="Sys-Settings-tenant-selection-title">Select Clinic</h2>
            <select 
              value={selectedTenant} 
              onChange={(e) => setSelectedTenant(e.target.value)} 
              disabled={isLoadingTenants} 
              className="Sys-Settings-tenant-select"
            >
              <option value="">-- Select Clinic --</option>
              {tenants.map(tenant => (
                <option key={tenant._id} value={tenant._id}>{tenant.name}</option>
              ))}
            </select>
          </div>

          {selectedTenant && !isLoading && (
            <>
              {/* Platform Settings */}
              <div className="platform-settings">
                <div className="setting-group">
                  <label className="header-setting-group-label">General Settings</label>
                   <div className="system-mood-header-divider"></div>
                  <label className="setting-label">Platform Name</label>
                  <div className="setting-input-group">
                    <input 
                      type="text" 
                      value={settings.platformName} 
                      onChange={(e) => handleInputChange('platformName', e.target.value)} 
                      placeholder="Enter platform name" 
                      className="setting-input"
                    />
                    <button 
                      onClick={() => saveIndividualSetting('platformName', settings.platformName)} 
                      className="save-button"
                    >
                      Save and Update
                    </button>
                  </div>
                </div>
                <div className="setting-group">
                  <label className="setting-label">Platform Description</label>
                  <div className="setting-input-group-textarea">
                    <textarea 
                      value={settings.platformDescription} 
                      onChange={(e) => handleInputChange('platformDescription', e.target.value)} 
                      placeholder="Enter platform description" 
                      rows={4} 
                      className="setting-textarea"
                    />
                    <button 
                      onClick={() => saveIndividualSetting('platformDescription', settings.platformDescription)} 
                      className="save-button"
                    >
                      Save and Update
                    </button>
                  </div>
                </div>
              

              {/* Logo Section */}
                  <div className="logo-grid">
                  {/* System Logo Light */}
                  <div className="logo-section">
                    <h3 className="logo-title">System Logo (Version 1 - Light)</h3>
                    <div style={{display: 'flex', alignItems: 'flex-end', gap: '24px'}}>
                      <div className="logo-preview">
                        {uploadingSection === 'lightLogo' ? (
                          <div className="logo-upload-loading">
                            <div className="logo-upload-spinner" />
                            <span>Uploading...</span>
                          </div>
                        ) : (previewUrls.lightLogo || settings.systemLogo?.light) ? (
                          <img
                            src={previewUrls.lightLogo || settings.systemLogo?.light}
                            alt="Light Logo"
                            className="logo-preview-image"
                          />
                        ) : (
                          <div className="logo-placeholder">
                            <div className="logo-placeholder-icon">
                              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="3" y="3" width="18" height="18" rx="2" stroke="#9ca3af" strokeWidth="1.5"/>
                                <circle cx="8.5" cy="8.5" r="1.5" stroke="#9ca3af" strokeWidth="1.5"/>
                                <path d="M21 15L16 10L7 19" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <div>NEUROLEX_Logo_Light.png</div>
                          </div>
                        )}
                      </div>
                      <div>
                        {selectedFileNames.lightLogo && (
                          <div className="file-name-display">
                            {selectedFileNames.lightLogo}
                          </div>
                        )}
                        <button
                          className="change-image-button"
                          onClick={() => document.getElementById('light-logo-input').click()}
                          disabled={uploadingSection === 'lightLogo'}
                        >
                          {uploadingSection === 'lightLogo' ? 'Uploading...' : 'Change Image'}
                        </button>
                      </div>
                    </div>
                    <input
                      id="light-logo-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          setSelectedFileNames(prev => ({...prev, lightLogo: e.target.files[0].name}));
                          handleFileUpload('logo', 'light', e.target.files[0]);
                        }
                      }}
                      className="hidden-file-input"
                    />
                  </div>

                  {/* System Logo Dark */}
                  <div className="logo-section">
                    <h3 className="logo-title">System Logo (Version 1 - Dark)</h3>
                    <div style={{display: 'flex', alignItems: 'flex-end', gap: '24px'}}>
                      <div className="logo-preview logo-preview-dark">
                        {uploadingSection === 'darkLogo' ? (
                          <div className="logo-upload-loading">
                            <div className="logo-upload-spinner logo-upload-spinner-dark" />
                            <span className="logo-upload-loading-dark">Uploading...</span>
                          </div>
                        ) : (previewUrls.darkLogo || settings.systemLogo?.dark) ? (
                          <img
                            src={previewUrls.darkLogo || settings.systemLogo?.dark}
                            alt="Dark Logo"
                            className="logo-preview-image"
                          />
                        ) : (
                          <div className="logo-placeholder">
                            <div className="logo-placeholder-icon">
                              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="3" y="3" width="18" height="18" rx="2" stroke="#6b7280" strokeWidth="1.5"/>
                                <circle cx="8.5" cy="8.5" r="1.5" stroke="#6b7280" strokeWidth="1.5"/>
                                <path d="M21 15L16 10L7 19" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <div>NEUROLEX_Logo_Dark.png</div>
                          </div>
                        )}
                      </div>
                      <div>
                        {selectedFileNames.darkLogo && (
                          <div className="file-name-display">
                            {selectedFileNames.darkLogo}
                          </div>
                        )}
                        <button
                          className="change-image-button"
                          onClick={() => document.getElementById('dark-logo-input').click()}
                          disabled={uploadingSection === 'darkLogo'}
                        >
                          {uploadingSection === 'darkLogo' ? 'Uploading...' : 'Change Image'}
                        </button>
                      </div>
                    </div>
                    <input
                      id="dark-logo-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          setSelectedFileNames(prev => ({...prev, darkLogo: e.target.files[0].name}));
                          handleFileUpload('logo', 'dark', e.target.files[0]);
                        }
                      }}
                      className="hidden-file-input"
                    />
                  </div>

                  {/* Favicon Light */}
                  <div className="logo-section">
                    <h3 className="logo-title">Favicon (Light)</h3>
                    <div style={{display: 'flex', alignItems: 'flex-end', gap: '24px'}}>
                      <div className="logo-preview">
                        {uploadingSection === 'lightFavicon' ? (
                          <div className="logo-upload-loading">
                            <div className="logo-upload-spinner" />
                            <span>Uploading...</span>
                          </div>
                        ) : (previewUrls.lightFavicon || settings.favicon?.light) ? (
                          <img
                            src={previewUrls.lightFavicon || settings.favicon?.light}
                            alt="Light Favicon"
                            className="logo-preview-image"
                          />
                        ) : (
                          <div className="logo-placeholder">
                            <div className="logo-placeholder-icon">
                              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19 21L12 16L5 21V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H17C17.5304 3 18.0391 3.21071 18.4142 3.58579C18.7893 3.96086 19 4.46957 19 5V21Z" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <div>NEUROLEX_Favicon_Light.ico</div>
                          </div>
                        )}
                      </div>
                      <div>
                        {selectedFileNames.lightFavicon && (
                          <div className="file-name-display">
                            {selectedFileNames.lightFavicon}
                          </div>
                        )}
                        <button
                          className="change-image-button"
                          onClick={() => document.getElementById('light-favicon-input').click()}
                          disabled={uploadingSection === 'lightFavicon'}
                        >
                          {uploadingSection === 'lightFavicon' ? 'Uploading...' : 'Change Image'}
                        </button>
                      </div>
                    </div>
                    <input
                      id="light-favicon-input"
                      type="file"
                      accept="image/*,.ico"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          setSelectedFileNames(prev => ({...prev, lightFavicon: e.target.files[0].name}));
                          handleFileUpload('favicon', 'light', e.target.files[0]);
                        }
                      }}
                      className="hidden-file-input"
                    />
                  </div>

                  {/* Favicon Dark */}
                  <div className="logo-section">
                    <h3 className="logo-title">Favicon (Dark)</h3>
                    <div style={{display: 'flex', alignItems: 'flex-end', gap: '24px'}}>
                      <div className="logo-preview logo-preview-dark">
                        {uploadingSection === 'darkFavicon' ? (
                          <div className="logo-upload-loading">
                            <div className="logo-upload-spinner logo-upload-spinner-dark" />
                            <span className="logo-upload-loading-dark">Uploading...</span>
                          </div>
                        ) : (previewUrls.darkFavicon || settings.favicon?.dark) ? (
                          <img
                            src={previewUrls.darkFavicon || settings.favicon?.dark}
                            alt="Dark Favicon"
                            className="logo-preview-image"
                          />
                        ) : (
                          <div className="logo-placeholder">
                            <div className="logo-placeholder-icon">
                              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19 21L12 16L5 21V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H17C17.5304 3 18.0391 3.21071 18.4142 3.58579C18.7893 3.96086 19 4.46957 19 5V21Z" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <div>NEUROLEX_Favicon_Dark.ico</div>
                          </div>
                        )}
                      </div>
                      <div>
                        {selectedFileNames.darkFavicon && (
                          <div className="file-name-display">
                            {selectedFileNames.darkFavicon}
                          </div>
                        )}
                        <button
                          className="change-image-button"
                          onClick={() => document.getElementById('dark-favicon-input').click()}
                          disabled={uploadingSection === 'darkFavicon'}
                        >
                          {uploadingSection === 'darkFavicon' ? 'Uploading...' : 'Change Image'}
                        </button>
                      </div>
                    </div>
                    <input
                      id="dark-favicon-input"
                      type="file"
                      accept="image/*,.ico"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          setSelectedFileNames(prev => ({...prev, darkFavicon: e.target.files[0].name}));
                          handleFileUpload('favicon', 'dark', e.target.files[0]);
                        }
                      }}
                      className="hidden-file-input"
                    />
                      </div>
                  </div>
              </div>

{/* 🆕 ENHANCED: HIRS Management Table with Toggle Functionality */}
<div className="hirs-management">
  <div className="hirs-header">
    <div>
      <h2 className="hirs-title">Doctor Navigation Menu Control</h2>
    </div>
  </div>

  <div className="hirs-table-container">
    <div className="hirs-table">
      {/* Table Header */}
      <div className="hirs-table-header">
        <div>Icon</div>
        <div>Menu Item</div>
        <div>Description</div>
        <div>Last Updated</div>
        <div>Status</div>
      </div>

      {/* Table Rows */}
      {settings.hirsSettings.map((hirs) => (
        <div key={hirs.id} className="hirs-table-row">
          <div className="hirs-icon">
            <img src={getIconForHirs(hirs.id)} alt={hirs.name} />
          </div>
          <div className="hirs-name">{hirs.name}</div>
          <div className="hirs-description">{hirs.description}</div>
          <div className="hirs-date">{hirs.lastUpdated}</div>
          <div className="hirs-status">
            <button
              onClick={() => handleHirsToggleClick(hirs)}
              disabled={modalState.isLoading || (hirs.id === 1)}
              className={`hirs-toggle-button ${
                hirs.id === 1 
                  ? 'hirs-toggle-button-locked' 
                  : hirs.isActive 
                    ? 'hirs-toggle-button-disable' 
                    : 'hirs-toggle-button-enable'
              }`}
              title={hirs.id === 1 ? 'Dashboard cannot be disabled (critical feature)' : `Click to ${hirs.isActive ? 'disable' : 'enable'} this feature for all doctors`}
            >
              {hirs.id === 1 ? (
                <>Always On</>
              ) : hirs.isActive ? (
                <>Disable</>
              ) : (
                <> Enable</>
              )}
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
</div>

              {/* Save Actions */}
              <div className="save-actions">
                <button 
                  onClick={resetSettings} 
                  disabled={isLoading || isSaving} 
                  className="reset-button"
                >
                  Reset Changes
                </button>
                <button 
                  onClick={saveSettings} 
                  disabled={isLoading || isSaving} 
                  className="save-all-button"
                >
                  {isSaving ? 'Saving...' : 'Save All Settings'}
                </button>
              </div>
            </>
          )}

          {/* Loading and Empty States */}
          {isLoading && (
            <div className="loading-state">
              <h3 className="loading-title">Loading Settings...</h3>
              <p className="loading-description">Fetching configuration for selected clinic...</p>
            </div>
          )}

          {!selectedTenant && !isLoadingTenants && (
            <div className="empty-state">
              <h3 className="empty-title">No Clinic Selected</h3>
              <p className="empty-description">Please select a clinic from the dropdown above to configure its settings.</p>
            </div>
          )}

        </div>

        {/* 🆕 NEW: HIRS Toggle Modal */}
        <HirsToggleModal
          isOpen={modalState.isOpen}
          onClose={handleModalClose}
          onConfirm={handleModalConfirm}
          hirsSetting={modalState.hirsSetting}
          action={modalState.action}
          isLoading={modalState.isLoading}
        />
      </div>
    </>
  );
};

export default SystemSettings;